import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useTranslations } from '../hooks/useTranslations';

interface CookieConsentModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const CookieConsentModal: React.FC<CookieConsentModalProps> = ({
  isOpen,
  onAccept,
  onDecline
}) => {
  const { t } = useTranslations();
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
            data-testid="cookie-consent-backdrop"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-consent-title"
              data-testid="cookie-consent-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                  <h2
                    id="cookie-consent-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    {t('cookie_title')}
                  </h2>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  {t('cookie_body1')}
                </p>

                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  <strong>{t('cookie_importantLabel')}</strong>{t('cookie_importantText')}
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-blue-800 text-xs">
                    <strong>{t('cookie_storeLabel')}</strong>{t('cookie_storeText')}
                    <br />
                    <strong>{t('cookie_dontLabel')}</strong>{t('cookie_dontText')}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={onAccept}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  data-testid="cookie-accept"
                >
                  {t('cookie_acceptCookies')}
                </button>

                <button
                  onClick={onDecline}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  data-testid="cookie-decline"
                >
                  {t('cookie_decline')}
                </button>
              </div>

              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  {t('cookie_rememberNote')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentModal;